import React, { useState } from 'react';
import { satisfaction as api } from '../services/api';
import { ISSUE_PROJET, CONTACT_PROJET } from './SatisfactionCommun';

/**
 * Suivi commercial d'un projet d'achat (2026-11-02).
 *
 * Le client a répondu qu'il envisage de changer de véhicule : on le rappelle,
 * puis on note l'issue (RDV pris, injoignable, pas de projet finalement).
 * C'est indépendant du traitement de l'avis : l'encart `BlocProjet` vit dans
 * la fiche réponse, avec ses propres boutons, et ne se valide jamais avec
 * l'avis. Les deux onglets (Réponses, Projets d'achat) ouvrent cette même
 * fiche : le commercial qui rappelle un projet voit aussi la note et le
 * commentaire du client (un client mécontent de l'atelier ne s'appelle pas
 * comme un client ravi).
 * Sans accord de recontact, aucune issue ne s'enregistre : on ne démarche pas.
 */

const ISSUES = [
  { cle: 'rdv_pris', label: '📅 RDV pris' },
  { cle: 'injoignable', label: '📵 Injoignable' },
  { cle: 'sans_suite', label: '🚫 Pas de projet' },
];

function fmtDate(d) {
  if (!d) return '';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '' : x.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Pastille d'état du projet, pour un en-tête ou une ligne de liste.
export function EtatProjet({ statut, joignable }) {
  if (!joignable) return <span className="sat-pa-etat sat-pa-etat-muet">🚗 Projet · ne pas démarcher</span>;
  const issue = ISSUE_PROJET[statut];
  if (!issue || statut === 'injoignable') {
    return <span className="sat-pa-etat">🚗 Projet à rappeler{statut === 'injoignable' ? ' (injoignable)' : ''}</span>;
  }
  return <span className="sat-pa-etat sat-pa-etat-fait">🚗 Projet : {issue.label}</span>;
}

/**
 * Encart « Projet d'achat ». `p` porte les champs du projet (choix, joignable,
 * projet_statut, projet_note, projet_traite_le, projet_par_*) et les
 * coordonnées du client.
 */
export function BlocProjet({ envoiId, p, user, onSaved }) {
  const [note, setNote] = useState(p.projet_note || '');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const issue = ISSUE_PROJET[p.projet_statut];
  const contacts = CONTACT_PROJET.filter((c) => p.choix?.[c.cle]);

  const enregistrer = async (statut) => {
    setEnCours(true);
    setErreur('');
    try {
      await api.suivreProjet(envoiId, { statut, note });
      onSaved();
    } catch (e) {
      setErreur(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <section className={`sat-encart sat-pa-encart${p.joignable ? '' : ' sat-pa-encart-muet'}`}>
      <div className="sat-encart-tete">
        <h3>🚗 Projet d'achat</h3>
        <EtatProjet statut={p.projet_statut} joignable={p.joignable} />
      </div>
      <p className="sat-encart-aide">
        Le client envisage de changer de véhicule dans les 12 mois. Ce suivi commercial se note ici,
        <b> à part du traitement de l'avis</b>.
      </p>

      {p.joignable ? (
        <>
          <div className="sat-pa-bloc">
            <div className="sat-pa-titre">Accepte d'être recontacté par</div>
            <div className="sat-pa-contacts">
              {contacts.map((c) => <span key={c.cle} className="badge-pill">{c.label}</span>)}
            </div>
            <div className="sat-pa-coord">
              {p.client_telephone && <a href={`tel:${p.client_telephone}`}>{p.client_telephone}</a>}
              {p.client_telephone && !p.choix?.prospection_tel && <span className="sat-pa-alerte">SMS uniquement : ne pas appeler</span>}
              {p.client_email && <span>{p.client_email}</span>}
            </div>
          </div>

          {issue && (
            <div className="sat-meta">
              Dernière issue : <span className={issue.classe}>{issue.label}</span>
              {p.projet_par_prenom && ` · ${p.projet_par_prenom} ${(p.projet_par_nom || '').charAt(0)}.`}
              {p.projet_traite_le && ` · ${fmtDate(p.projet_traite_le)}`}
            </div>
          )}

          <label className="sat-pa-bloc" htmlFor={`sat-pa-note-${envoiId}`}>
            <span className="sat-pa-titre">Ce qui a été dit sur le projet</span>
            <textarea id={`sat-pa-note-${envoiId}`} className="sat-input" rows={3} value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Modèle recherché, échéance, date du RDV, vendeur…" />
          </label>
          {erreur && <div className="status-error sat-bandeau">{erreur}</div>}
          <div className="sat-pa-issues">
            {ISSUES.map((i) => (
              <button key={i.cle} type="button" className="btn-secondary sat-pa-issue"
                disabled={enCours} onClick={() => enregistrer(i.cle)}>
                {i.label}
              </button>
            ))}
            {issue && user?.role === 'super-admin' && (
              <button type="button" className="sat-f-lien" disabled={enCours} onClick={() => enregistrer(null)}>
                Remettre à rappeler
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="sat-pa-interdit">
          Le client n'a pas donné son accord pour être recontacté sur son projet : <b>ne pas le démarcher</b>.
        </div>
      )}
    </section>
  );
}
